/**
 * Payment-Link Link-Tester — Prüf-Logik (Spec 501, Phase 6).
 *
 * Reine, teaser-sichere Verifikations-Logik fuer Modus B. Wird nur bei geladenem
 * Format aufgerufen (der Aufrufer gated auf `format.available`); im Teaser gibt es
 * kein Urteil (Spec §Teaser-Verhalten).
 */
import { computeHmacSha256Hex, normalizeSignature } from './hmac-engine';
import { computeFingerprints, type FingerprintResult } from './fingerprint';
import { buildFullUrl } from './link-url';
import type { LinkParam, PaymentLinkFormatDefinition } from './format-definition';
import type { PayStageEntry } from './stage-map';

/** Index der ersten abweichenden Stelle zweier Strings, sonst -1 (identisch). */
export function firstDiffIndex(a: string, b: string): number {
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i += 1) {
    if (a[i] !== b[i]) {
      return i;
    }
  }
  return -1;
}

export type VerifyState = 'valid' | 'invalid' | 'inputError';

export interface VerifyOutcome {
  state: VerifyState;
  /** Kanonischer String-to-Sign (fuer die Didaktik). */
  stringToSign?: string;
  /** Erwartete Signatur (bei valid/invalid). */
  expected?: string;
  /** Normalisierte gelieferte Signatur. */
  received?: string;
  /** Fingerprint-Treffer (nur invalid). */
  fingerprints?: FingerprintResult[];
  /** Korrigierter, kopierbarer Link („So wäre er richtig", nur invalid). */
  correctedUrl?: string;
  /**
   * Sonderfall: die Signatur ist INHALTLICH korrekt, aber der Wire-Wert enthält
   * Großbuchstaben-Hex — der Dienst lehnt das ab (Handover §5). Erkannt auf der
   * ROHEN Eingabe (die Normalisierung lowercased für den Vergleich). Der
   * korrigierte Link trägt die Kleinbuchstaben-Signatur.
   */
  uppercaseSignatureRejected?: boolean;
  /** Grund eines Eingabefehlers (kein Mismatch-Urteil). */
  inputError?:
    | 'empty'
    | 'notHex'
    | 'wrongLength'
    | 'missingSignature'
    | 'formatError';
}

/**
 * Prueft einen geparsten Link gegen den Salt. Klassifiziert die Parameter ueber
 * das Format, baut den kanonischen String-to-Sign, rechnet die erwartete Signatur
 * und vergleicht. Bei Mismatch werden die Fehler-Varianten gefingerprintet und ein
 * korrigierter Link gebaut. Ungueltige Signatur-EINGABEN (leere/falsche Laenge/
 * Nicht-Hex, fehlender Signatur-Parameter) sind Eingabefehler, kein Mismatch.
 */
export async function verifyLink(
  format: PaymentLinkFormatDefinition,
  salt: string,
  baseEntry: PayStageEntry,
  parsed: { params: LinkParam[]; signature?: string },
): Promise<VerifyOutcome> {
  if (parsed.signature === undefined) {
    return { state: 'inputError', inputError: 'missingSignature' };
  }
  const norm = normalizeSignature(parsed.signature);
  if (!norm.ok) {
    return { state: 'inputError', inputError: norm.reason };
  }

  // Never-throw: eine (kuenftig eingesteckte) fehlerhafte Format-Definition darf
  // keine unhandled rejection ausloesen — und der Fehlerkanal ist der klassische
  // Ort fuer versehentliche Eingabe-Leaks. Daher neutraler `formatError`-Zustand,
  // der weder Salt noch Fehlermeldung durchreicht.
  try {
    // WIRE-Reihenfolge erhalten: die geparsten Parameter werden in ORIGINAL-
    // Reihenfolge durchgereicht (systemParams), NICHT in signiert/frei gebucketet.
    // Unter „c=1" ist der frühere Split kosmetisch (`buildStringToSign` byte-
    // sortiert ohnehin), aber ein Bucketing würde die Wire-Reihenfolge zerstören,
    // die der `sortWire`-Fingerprint braucht (interleavte System-/rohe Parameter).
    // V6 (`rawOmitted`) bestimmt „raw" intern über `bodyReachOf`, nicht über den Split.
    const inputs: { systemParams: LinkParam[]; freeParams: LinkParam[] } = {
      systemParams: parsed.params,
      freeParams: [],
    };

    const stringToSign = format.buildStringToSign(inputs);
    const expected = await computeHmacSha256Hex(salt, stringToSign);

    // Großbuchstaben-`sig`-Sonderfall: auf der ROHEN Eingabe (nur Whitespace/
    // `0x`/`sha256=`-Präfix entfernt, NICHT lowercased) prüfen, ob Großbuchstaben-
    // Hex vorliegt. Der Dienst lehnt das ab, auch wenn der Inhalt stimmt.
    const rawStripped = parsed.signature
      .replace(/\s+/g, '')
      .replace(/^(0x|sha256=)/i, '');
    const hasUppercaseHex = /[A-F]/.test(rawStripped);

    if (norm.hex === expected) {
      if (hasUppercaseHex) {
        // Inhaltlich korrekt, aber Wire-Wert wird abgelehnt → kein „valid".
        const correctedUrl = buildFullUrl(
          baseEntry,
          parsed.params,
          format.signatureParamName,
          expected,
          format.signaturePlacement,
          format.clickPath ?? '',
        );
        return {
          state: 'invalid',
          stringToSign,
          expected,
          received: norm.hex,
          uppercaseSignatureRejected: true,
          fingerprints: [],
          correctedUrl,
        };
      }
      return { state: 'valid', stringToSign, expected, received: norm.hex };
    }

    const fingerprints = await computeFingerprints(
      format,
      salt,
      inputs,
      norm.hex,
    );
    const correctedUrl = buildFullUrl(
      baseEntry,
      parsed.params,
      format.signatureParamName,
      expected,
      format.signaturePlacement,
      format.clickPath ?? '',
    );

    return {
      state: 'invalid',
      stringToSign,
      expected,
      received: norm.hex,
      fingerprints,
      correctedUrl,
    };
  } catch {
    return { state: 'inputError', inputError: 'formatError' };
  }
}
